import React, { useEffect, useRef, useState } from 'react'
import { Text, View, Modal } from 'react-native'
import { Image } from 'expo-image'
import { Feather } from '@expo/vector-icons'
import { FormContentsModel, ModelAnswerFromProps } from '../models/StoreDetailModel'
import { TouchableOpacity } from 'react-native-gesture-handler'
import theme from '../../../../theme'
import styles from './InputPhotoItemStyles'
import { updateFormAnswerSchedule } from '../../../../db-sqlite/repositories/schedule.repository'
import { Ionicons } from '@expo/vector-icons'
import { eventEmitter, get_url_save_photo } from '../../../utils/functions.utils'
import { getLanguage } from '../../appBar/context/LocalizationContext'
import { PhotoModal } from './InputPhotoItem'
import { useBottomSheet } from '../../customUtils/context/BottomSheetContext'
import { ImageWithZoom } from '../../customUtils/Components/ImageWIthZoom'

interface PhotoEntry {
    uri: string
    filename: string
}

interface InputPhotoMultipleItemProps {
    item: FormContentsModel
    form_id: string
    schedule_id: string
    store_id: string
    checkComplete: (option?: ModelAnswerFromProps) => void
}

export const InputPhotoMultipleItem = React.memo(
    ({
        item,
        form_id,
        schedule_id,
        store_id,
        checkComplete,
    }: InputPhotoMultipleItemProps) => {
        const { languageFile } = getLanguage()
        const [entries, setEntries] = useState<PhotoEntry[]>([])
        const entriesRef = useRef<PhotoEntry[]>([])
        const [showCameraModal, setShowCameraModal] = useState(false)
        const { openBottomSheet } = useBottomSheet()
        const placeHolder = require('../../../../assets/placeholderImage.png')
        const maxPhotos =
            parseInt(item.label) || parseInt(item.input_type.label) || 1

        useEffect(() => {
            // Solo restaurar si el usuario no tiene fotos activas en esta sesión.
            // Cuando checkComplete() propaga el answer al padre, item.old_response
            // se actualiza y dispara este effect, pero old_response.medias contiene
            // filenames (no URIs file://), lo que sobrescribiría entries con datos inválidos.
            if (item?.old_response && entriesRef.current.length === 0) {
                const restored: PhotoEntry[] = []
                if (item.old_response.pickerResult_uri) {
                    restored.push({
                        uri: item.old_response.pickerResult_uri,
                        filename: item.old_response.media ?? '',
                    })
                }
                if (Array.isArray(item.old_response.medias)) {
                    item.old_response.medias.forEach((uri) => {
                        if (uri) restored.push({ uri, filename: uri })
                    })
                }
                if (restored.length > 0) {
                    entriesRef.current = restored
                    setEntries(restored)
                }
            }
        }, [item?.old_response])

        const buildAndSave = (updatedEntries: PhotoEntry[]) => {
            let answerScore = '0'
            if (item.score != null && item.score > 0)
                answerScore = item.score.toString()

            const first = updatedEntries[0]
            const rest = updatedEntries.slice(1)

            const answer: ModelAnswerFromProps = {
                _id: item._id,
                option_selected_text: [],
                option_selected_id: [],
                question_id: item._id,
                media: first?.filename ?? '',
                question_text: item.title,
                score: answerScore,
                status: 1,
                date_finish: new Date().toString(),
                date_init: new Date().toString(),
                question_input: '',
                field_media: '',
                field_text: '',
                questionScore: item.questionScore,
                type: item.input_type.type,
                ponderar: item.score && item.score > 0,
                question: item.title,
                questionOptionSelected: [],
                questionOptionSelectedText: [],
                questionType: {
                    title: item.input_type.title,
                    type: item.input_type.type,
                    id: item.input_type._id,
                },
                questionPrice: '',
                description: '',
                tag: '',
                questionImage: first?.filename ?? '',
                questionInput: '',
                pickerResult_uri: first?.uri ?? '',
                medias: rest.map((e) => e.filename),
                data_medias: rest.map((e) => ({
                    filename: e.filename,
                    pickerResult_uri: e.uri,
                })),
            }

            updateFormAnswerSchedule({
                newAnswer: answer,
                schedule_id,
                form_id,
                store_id,
            })
            checkComplete(answer)
        }

        const handleImagePickerResult = async (photoUri: string) => {
            try {
                eventEmitter.emit('process_start_image')
                if (photoUri) {
                    const filename = get_url_save_photo('/stores/form')
                    // Usar ref para evitar stale closure cuando se seleccionan múltiples fotos
                    const updatedEntries = [
                        ...entriesRef.current,
                        { uri: photoUri, filename },
                    ]
                    entriesRef.current = updatedEntries
                    setEntries(updatedEntries)
                    buildAndSave(updatedEntries)
                }
            } catch (error) {
                console.error(
                    'InputPhotoMultipleItem handleImagePickerResult error:',
                    error,
                )
            } finally {
                eventEmitter.emit('process_end_image')
                setShowCameraModal(false)
            }
        }

        const deleteImage = (index: number) => {
            const updatedEntries = entries.filter((_, i) => i !== index)
            entriesRef.current = updatedEntries
            setEntries(updatedEntries)

            if (updatedEntries.length === 0) {
                const answer: ModelAnswerFromProps = {
                    _id: item._id,
                    option_selected_text: [],
                    option_selected_id: [],
                    question_id: item._id,
                    media: null,
                    question_text: item.title,
                    score: '0',
                    status: 1,
                    date_finish: new Date().toString(),
                    date_init: new Date().toString(),
                    question_input: '',
                    field_media: '',
                    field_text: '',
                    questionScore: item.questionScore,
                    type: item.input_type.type,
                    ponderar: item.questionScore && item.questionScore > 0,
                    question: item.title,
                    questionOptionSelected: [],
                    questionOptionSelectedText: [],
                    questionType: {
                        title: item.input_type.title,
                        type: item.input_type.type,
                        id: item.input_type._id,
                    },
                    questionPrice: '',
                    description: '',
                    tag: '',
                    questionImage: '',
                    questionInput: '',
                    medias: [],
                }
                updateFormAnswerSchedule({
                    newAnswer: answer,
                    schedule_id,
                    form_id,
                    store_id,
                })
                checkComplete(answer)
            } else {
                buildAndSave(updatedEntries)
            }
        }

        const openBigImage = (uri: string) => {
            if (uri) {
                openBottomSheet(
                    <ImageWithZoom uri={uri} contentSeen={() => {}} path={''} />,
                    undefined,
                    undefined,
                    true,
                    true,
                    true,
                )
            }
        }

        return (
            <View>
                <Text style={styles.title}>{item.title}</Text>

                {entries.map((entry, index) => (
                    <View
                        key={index}
                        style={[styles.containerGeneral, { marginBottom: 8 }]}
                    >
                        <View style={[styles.subContainer, { flex: 1.2 }]}>
                            <TouchableOpacity
                                onPress={() => openBigImage(entry.uri)}
                            >
                                <Image
                                    style={styles.imagePreview}
                                    source={entry.uri}
                                />
                            </TouchableOpacity>
                            <Text style={[styles.fileName, { flex: 1 }]}>
                                {`Foto ${index + 1}/${maxPhotos}`}
                            </Text>
                            <Ionicons
                                name={'checkmark-sharp'}
                                size={16}
                                color={theme.colors.colorApproved}
                            />
                        </View>
                        <View style={{ width: 12 }} />
                        <View>
                            <TouchableOpacity
                                onPress={() => deleteImage(index)}
                                style={styles.containerMiniButtonDelete}
                            >
                                <Text style={styles.textMiniButtonDelete}>
                                    {languageFile.delete}
                                </Text>
                                <Feather
                                    name='trash-2'
                                    size={12}
                                    color={theme.colors.error}
                                    style={styles.marginLeft}
                                />
                            </TouchableOpacity>
                        </View>
                    </View>
                ))}

                {entries.length === 0 && (
                    <View style={styles.containerGeneral}>
                        <View style={styles.subContainer}>
                            <Image
                                style={[
                                    styles.imagePreview,
                                    { width: 80, height: 80 },
                                ]}
                                source={placeHolder}
                            />
                            <Text style={styles.fileName}>
                                {languageFile.upload_image}
                            </Text>
                        </View>
                        <View style={{ flex: 0.5 }}>
                            <TouchableOpacity
                                onPress={() => setShowCameraModal(true)}
                                style={[
                                    styles.containerMiniButton,
                                    { justifyContent: 'center' },
                                ]}
                            >
                                <Text style={styles.textMiniButton}>
                                    {languageFile.photo}
                                </Text>
                                <Feather
                                    name='plus-square'
                                    size={14}
                                    color={theme.colors.colorPrimary}
                                    style={styles.marginLeftBis}
                                />
                            </TouchableOpacity>
                        </View>
                    </View>
                )}

                {entries.length > 0 && entries.length < maxPhotos && (
                    <TouchableOpacity
                        onPress={() => setShowCameraModal(true)}
                        style={[
                            styles.containerMiniButton,
                            {
                                justifyContent: 'center',
                                marginTop: 8,
                                marginHorizontal: 0,
                            },
                        ]}
                    >
                        <Text style={styles.textMiniButton}>
                            {`Agregar foto (${entries.length + 1}/${maxPhotos})`}
                        </Text>
                        <Feather
                            name='plus-square'
                            size={14}
                            color={theme.colors.colorPrimary}
                            style={styles.marginLeftBis}
                        />
                    </TouchableOpacity>
                )}

                {showCameraModal && (
                    <PhotoModal
                        hasCamera={true}
                        hasGalery={false}
                        handleImagePickerResult={handleImagePickerResult}
                    />
                )}
            </View>
        )
    },
)
